/**
 * Kava Society anchor links
 *
 * Lightweight progressive enhancement that adds in-page anchor navigation
 * to long content pages without requiring per-page setup.
 *
 * What it does:
 *   1. Slugifies all H2 and H3 visible text into DOM ids on those headings.
 *      Strips leading "1." style numeric prefixes so "1. About Kava" -> "about-kava".
 *   2. Resolves any <a href="#something"> links on the page against those slugs.
 *      Matches: exact slug, first-word slug, or fuzzy substring on either side.
 *   3. Smooth-scrolls on click and respects URL hash on initial page load.
 *
 * Failure mode: any error is caught and logged; the page is unaffected.
 */
(function () {
  'use strict';

  function slugify(text) {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/^\s*\d+\.\s*/, '')   // strip leading "1. " / "2. " etc.
      .replace(/[^a-z0-9\s-]/g, '')  // drop punctuation
      .trim()
      .replace(/\s+/g, '-')          // spaces -> hyphens
      .replace(/-+/g, '-')           // collapse runs of hyphens
      .replace(/^-+|-+$/g, '');      // trim hyphens
  }

  function init() {
    try {
      // 1) Assign slugified ids to heading elements inside main content only.
      //    Prestige renders most "headings" as <p class="h2"> etc., so we
      //    pick up both real heading tags and class-based ones, but stay
      //    out of the header/footer/nav/cart-drawer to avoid polluting
      //    nav-only labels like "Country" or "Cart".
      //
      //    When two headings have the same slug (e.g. an h3 tab label and
      //    an h2 section heading both reading "Instant Kava"), prefer the
      //    higher-priority heading (h2 > h3 > h4) so anchor links land on
      //    the real section, not on a small repeated label.
      var slugToId = {};
      var firstWordToId = {};

      function priority(el) {
        if (el.tagName === 'H1' || el.classList.contains('h1')) return 1;
        if (el.tagName === 'H2' || el.classList.contains('h2')) return 2;
        if (el.tagName === 'H3' || el.classList.contains('h3')) return 3;
        if (el.tagName === 'H4' || el.classList.contains('h4')) return 4;
        return 5;
      }

      var contentRoot = document.querySelector('main') || document.body;
      var headingsList = contentRoot.querySelectorAll(
        'h1, h2, h3, h4, .h1, .h2, .h3, .h4'
      );
      // Stable sort by priority — preserves doc order within each tier.
      var sorted = Array.prototype.slice.call(headingsList);
      sorted.sort(function (a, b) { return priority(a) - priority(b); });

      for (var i = 0; i < sorted.length; i++) {
        var h = sorted[i];
        var slug = slugify(h.textContent || h.innerText || '');
        if (!slug) continue;
        // If this slug already has a winner from a higher-priority heading,
        // skip — leaves this lower-priority element without an id, so that
        // getElementById(slug) returns only the winning element.
        if (slugToId[slug]) continue;
        if (!h.id) h.id = slug;
        slugToId[slug] = h.id;
        var firstWord = slug.split('-')[0];
        if (firstWord && !firstWordToId[firstWord]) {
          firstWordToId[firstWord] = h.id;
        }
      }

      // 2) Resolve anchor links by text/slug match.
      //    Match strategies, in order of specificity. The fuzzy step is
      //    deliberately tight (starts-with on the heading slug) so a link
      //    like "#traditional" matches "Traditional Grind Kava" but NOT
      //    "Tips for Traditional Grind".
      var anchorLinks = document.querySelectorAll('a[href^="#"]');
      for (var j = 0; j < anchorLinks.length; j++) {
        var link = anchorLinks[j];
        var href = link.getAttribute('href');
        if (!href || href === '#') continue;
        var target = href.slice(1);
        if (!target) continue;

        // Skip if the target id already exists in the DOM (heading already had id).
        if (document.getElementById(target)) continue;

        var resolved = null;
        var linkSlug = slugify(link.textContent || '');

        if (slugToId[linkSlug]) {
          resolved = slugToId[linkSlug];          // exact slug from link text
        } else if (slugToId[target]) {
          resolved = slugToId[target];            // exact slug from href target
        } else if (firstWordToId[target]) {
          resolved = firstWordToId[target];       // target equals first word of a heading
        } else {
          // Tight prefix match: heading slug must START WITH target+"-".
          // Prevents e.g. "#traditional" matching "tips-for-traditional-grind".
          var keys = Object.keys(slugToId);
          for (var k = 0; k < keys.length; k++) {
            if (keys[k] === target || keys[k].indexOf(target + '-') === 0) {
              resolved = slugToId[keys[k]];
              break;
            }
          }
        }

        if (resolved) {
          link.setAttribute('href', '#' + resolved);
        }
      }

      // 3) Smooth-scroll on click for any in-page anchor.
      document.addEventListener('click', function (e) {
        var t = e.target;
        while (t && t.nodeType === 1 && t.tagName !== 'A') t = t.parentNode;
        if (!t || t.tagName !== 'A') return;
        var h = t.getAttribute('href');
        if (!h || h.charAt(0) !== '#' || h === '#') return;
        var el = document.getElementById(h.slice(1));
        if (!el) return;
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', h);
        }
      }, false);

      // 4) If page loads with a hash, scroll to it after layout settles.
      if (window.location.hash && window.location.hash.length > 1) {
        var initialTarget = document.getElementById(window.location.hash.slice(1));
        if (initialTarget) {
          setTimeout(function () {
            initialTarget.scrollIntoView({ behavior: 'auto', block: 'start' });
          }, 100);
        }
      }
    } catch (err) {
      if (window.console && window.console.warn) {
        window.console.warn('[anchor-links] init failed:', err);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, false);
  } else {
    init();
  }
})();
